import React from "react";
import {
  ParticipantData,
  SearchCriteria,
  SelectedappointmentControlsOption,
  ShowFileDialogProps,
} from "../interface/bookingOverviewInterface";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import { endpoints } from "../../../../endpoints/endpoints";
import { AppContext, User } from "../../../../context/appContext";

export const BookingOverviewContext = React.createContext<any>({});

const BookingOverviewProvider = ({ children }: any): React.JSX.Element => {
  const {
    user,
    checkSession,
  }: {
    user: User;
    checkSession: (response: any) => void;
  } = React.useContext(AppContext);

  const [participantData, setParticipantData] = React.useState<
    ParticipantData[] | null
  >(null);

  const [searchCriteria, setSearchCriteria] = React.useState<SearchCriteria>({
    partipantName: "",
    loading: false,
    date: "",
    coach: {
      label: "Alle",
      value: null,
    },
    status: {
      active: true,
      inactive: false,
    },
  });

  const startSearch = React.useCallback((): void => {
    setSearchCriteria({ ...searchCriteria, loading: true });

    axios
      .post(endpoints.admin.searchParticipantFull, {
        sessionId: user.sessionId,
        searchString: searchCriteria.partipantName,
        date: searchCriteria.date,
        coachId: searchCriteria.coach?.value,
      })
      .then((response) => {
        checkSession(response);

        setParticipantData(response.data.data);
        setSearchCriteria({ ...searchCriteria, loading: false });
      })
      .catch((error) => {
        setSearchCriteria({ ...searchCriteria, loading: false });
        console.log(error);
      });
  }, [checkSession, searchCriteria, user.sessionId]);

  const [showFileDialog, setShowFileDialog] =
    React.useState<ShowFileDialogProps>({
      open: false,
      participant: null,
    });

  const appointmentControlsOptions: SelectedappointmentControlsOption[] =
    React.useMemo(
      () => [
        {
          label: "Stornierung",
          value: 0,
        },
        {
          label: "Kulanz",
          value: 1,
        },
        {
          label: "Versäumt",
          value: 2,
        },
        {
          label: "Gebucht",
          value: 3,
        },
      ],
      []
    );

  const doCancellation = React.useCallback(
    (
      appointment: any,
      selectedOption: SelectedappointmentControlsOption | null,
      setSelectedOption: React.Dispatch<
        React.SetStateAction<SelectedappointmentControlsOption | null>
      >
    ) => {
      axios
        .post(endpoints.appointment.cancelByAdmin, {
          sessionId: user.sessionId,
          appointmentId: appointment.id,
          action: {
            cancelled: selectedOption?.value === 0,
            goodwill: selectedOption?.value === 1,
            missed: selectedOption?.value === 2,
            booked: selectedOption?.value === 3,
          },
        })
        .then((response) => {
          checkSession(response);

          setSelectedOption(null);

          enqueueSnackbar(
            response.data.meta.status === "success"
              ? "Änderung erfolgreich"
              : "Es ist ein Fehler",
            {
              variant: response.data.meta.status,
            }
          );

          startSearch();
        })
        .catch((error) => {
          console.log(error);
        });
    },
    [checkSession, startSearch, user.sessionId]
  );

  const value: {} = React.useMemo(
    () => ({
      participantData,
      setParticipantData,
      searchCriteria,
      setSearchCriteria,
      startSearch,
      showFileDialog,
      setShowFileDialog,
      appointmentControlsOptions,
      doCancellation,
    }),
    [
      appointmentControlsOptions,
      participantData,
      searchCriteria,
      showFileDialog,
      startSearch,
      doCancellation,
    ]
  );

  return (
    <BookingOverviewContext.Provider value={value}>
      {children}
    </BookingOverviewContext.Provider>
  );
};

export default BookingOverviewProvider;
